import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Instagram, Facebook } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { toEmbedSrc, embedKind } from "@/lib/embeds";
import { useSiteText, useSiteFlag, useSiteContent } from "@/context/SiteContentContext";
import { fmtEventDateShort } from "@/lib/dates";
import { allDisciplines, artistHasDiscipline, artistDisciplines } from "@/lib/artist";
import SceneSection from "@/components/SceneSection";
import MediaTile from "@/components/MediaTile";
import MediaRenderer, { derivePreset } from "@/components/MediaRenderer";
import Seo from "@/components/Seo";
import { ArtistTile } from "@/pages/Artists";
import QuickView from "@/components/QuickView";
import { scheme as schemeByName, schemeHex } from "@/lib/schemes";

const fmtDate = (iso) => iso; // kept for compat — now uses fmtEventDateShort per row

// Turn a section_scheme_<key> into (a) a Tailwind class string for bg+text and
// (b) a style object that overrides the layout's CSS variables so every child
// (including MediaRenderer canvas effects) picks up the section's palette.
function getSectionSchemeStyles(siteContent, key) {
    const name = (siteContent && siteContent[`section_scheme_${key}`]) || "";
    if (!name) return { className: "", style: {}, active: false };
    const sch = schemeByName(name);
    const hex = schemeHex(name);
    return {
        active: true,
        className: `${sch.bg} ${sch.text}`,
        style: {
            "--sc-bg": hex.bg,
            "--sc-text": hex.text,
            "--sc-subtext": hex.subtext,
            "--sc-accent": hex.accent,
            "--sc-accent2": hex.accent2,
            "--sc-border": hex.border,
            "--sc-card": hex.card,
        },
    };
}

// Standard section header — LEFT aligned: kicker (small mono) → big display
// title (same size as tienda/shop) → regular body text. Pass empty strings to
// hide any row; renders nothing when all are empty.
function SectionHeader({ kicker, title, body, testId }) {
    if (!kicker && !title && !body) return null;
    return (
        <div className="mb-8 md:mb-10 space-y-3 text-left" data-testid={testId}>
            {kicker && (
                <div className="font-mono text-xs md:text-sm uppercase tracking-[0.35em] text-[color:var(--sc-accent)]">{kicker}</div>
            )}
            {title && (
                <h2 className="itz-display font-heading font-black uppercase tracking-tighter leading-[0.95]">
                    {String(title).replace(/\.$/, "")}
                </h2>
            )}
            {body && (
                <p className="font-body text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-line max-w-2xl">{body}</p>
            )}
        </div>
    );
}

// Hashtag filter chips — same vibe as the /artists page filters.
function Chips({ options, value, onChange, testPrefix }) {
    const opts = (options || []).filter((o) => o && o.toLowerCase() !== "all");
    if (opts.length < 2) return null;
    return (
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-6" data-testid={`${testPrefix}-filters`}>
            {["all", ...opts].map((o) => (
                <button
                    key={o}
                    onClick={() => onChange(o)}
                    data-testid={`${testPrefix}-filter-${o}`}
                    className={`font-mono text-[11px] lowercase tracking-[0.2em] transition-colors ${value === o ? "text-[color:var(--sc-accent)]" : "opacity-60 hover:opacity-100"}`}
                >
                    #{o}
                </button>
            ))}
        </div>
    );
}

export default function Home() {
    const { t, lang, currency } = useT();
    const st = useSiteText(lang, t);
    const { content: siteContent } = useSiteContent();
    const showShop = useSiteFlag("section_show_shop", true);
    const showFeaturedArtist = useSiteFlag("section_show_featured_artist", true);
    const showArtists = useSiteFlag("section_show_artists", true);
    const showRosterGrid = useSiteFlag("section_show_roster_grid", true);
    const showEditorial = useSiteFlag("section_show_editorial", true);
    const showEvents = useSiteFlag("section_show_events", true);
    const showManifesto = useSiteFlag("section_show_manifesto", true);
    const showAbout = useSiteFlag("section_show_about", true);
    const showJoin = useSiteFlag("section_show_join", true);
    const showSubscribe = useSiteFlag("section_show_subscribe", true);
    const showCategories = useSiteFlag("section_show_categories", false);
    const showMediaPlayer = useSiteFlag("section_show_media_player", true);
    const showRadio = useSiteFlag("section_show_radio", true);
    const [artists, setArtists] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [shopSort, setShopSort] = useState("added");
    const [shopType, setShopType] = useState("all");
    const [shopArtist, setShopArtist] = useState("all");
    const [rosterGridFilter, setRosterGridFilter] = useState("all");
    const edSeed = useMemo(() => Math.floor(Math.random() * 997), []);
    const [events, setEvents] = useState([]);
    const [artistPreview, setArtistPreview] = useState(null);
    const [flyer, setFlyer] = useState(null);
    const [homeFilter, setHomeFilter] = useState("all");
    const [featuredFilter, setFeaturedFilter] = useState("all");
    const [quickItem, setQuickItem] = useState(null);
    const eventsRef = useRef(null);

    useEffect(() => {
        // Fetch ALL artists (not just featured=true) so home_featured_artist_slug can pick from anyone
        api.get("/artists").then((r) => setArtists(r.data));
        api.get("/products").then((r) => setTopProducts(r.data));
        api.get("/events", { params: { status: "upcoming" } }).then((r) => {
            const up = (r.data || []).slice(0, 3);
            if (up.length >= 3) setEvents(up);
            else api.get("/events", { params: { status: "past" } })
                .then((r2) => setEvents([...up, ...(r2.data || [])].slice(0, 3)))
                .catch(() => setEvents(up));
        });
    }, []);

    // Scroll to hash target (e.g. /#multimedia-lab from EXPERIMENTS nav link)
    useEffect(() => {
        if (!window.location.hash) return;
        const id = window.location.hash.replace("#", "");
        // Delay so sections have mounted
        const t = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
        return () => clearTimeout(t);
    }, []);

    // shop chips (home preview) — types + artists derived from products, plus sort
    const shopTypes = useMemo(() => {
        const set = new Set();
        topProducts.forEach((p) => {
            if (p.product_type) set.add(p.product_type);
            (p.tags || []).forEach((tg) => tg && set.add(tg));
        });
        return ["all", ...Array.from(set)];
    }, [topProducts]);
    const shopArtists = useMemo(() => {
        const set = new Set(topProducts.map((p) => p.artist_credit).filter(Boolean));
        return ["all", ...Array.from(set)];
    }, [topProducts]);
    const shopPreview = useMemo(() => {
        let list = topProducts.slice();
        if (shopType !== "all") list = list.filter((p) => p.product_type === shopType || (p.tags || []).includes(shopType));
        if (shopArtist !== "all") list = list.filter((p) => p.artist_credit === shopArtist);
        switch (shopSort) {
            case "price_asc":
                list.sort((a, b) => (a.price || 0) - (b.price || 0));
                break;
            case "price_desc":
                list.sort((a, b) => (b.price || 0) - (a.price || 0));
                break;
            case "added":
            default:
                list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        }
        return list.slice(0, 8);
    }, [topProducts, shopSort, shopType, shopArtist]);

    // Resolve featured artists: 1) all with home_featured=true (multiple supported), 2) site content slug pick, 3) any with embeds, 4) first
    const featuredList = useMemo(() => {
        if (!artists.length) return [];
        const marked = artists.filter((a) => a.home_featured);
        if (marked.length) return marked;
        const slugPick = siteContent.home_featured_artist_slug;
        if (slugPick) {
            const found = artists.find((a) => a.slug === slugPick);
            if (found) return [found];
        }
        const fallback =
            artists.find((a) => /quantaloop/i.test(a.name)) ||
            artists.find((a) => (a.embeds || []).length > 0) ||
            artists[0];
        return fallback ? [fallback] : [];
    }, [artists, siteContent.home_featured_artist_slug]);
    const featured = featuredList[0] || null;

    const handleEventMove = (e) => {
        if (!eventsRef.current) return;
        const rect = eventsRef.current.getBoundingClientRect();
        setFlyer((prev) => (prev ? { ...prev, x: e.clientX - rect.left, y: e.clientY - rect.top } : prev));
    };

    // roster (all artists, including featured — the featured card is just a highlight, not a removal)
    const rosterList = useMemo(() => artists.slice(0, 12), [artists]);
    // shop categories (admin-editable, up to 6) — same set used on /shop; reused here for home shop section
    const shopCategories = useMemo(() => (
        [1, 2, 3, 4, 5, 6]
            .map((n) => ({
                n,
                image: siteContent[`shop_category_${n}_image_url`] || "",
                label: siteContent[`shop_category_${n}_label_${lang}`] || siteContent[`shop_category_${n}_label_en`] || "",
                filter: siteContent[`shop_category_${n}_filter`] || "all",
            }))
            .filter((c) => c.image || c.label)
    ), [siteContent, lang]);
    const homeDisciplines = useMemo(() => allDisciplines(artists), [artists]);
    const filteredRoster = useMemo(
        () => (homeFilter === "all" ? rosterList : rosterList.filter((a) => artistHasDiscipline(a, homeFilter))),
        [rosterList, homeFilter]
    );
    // filter for the En Escena / Featured Artist section (independent from roster filter)
    const featuredDisciplines = useMemo(() => allDisciplines(featuredList), [featuredList]);
    const filteredFeatured = useMemo(
        () => (featuredFilter === "all" ? featuredList : featuredList.filter((a) => artistHasDiscipline(a, featuredFilter))),
        [featuredList, featuredFilter]
    );

    // Ordering: user-defined via `home_section_order` (comma-separated). Unknown/missing keys appended.
    // Default order matches the "editorial + Bandcamp" pivot: Hero → Night Vibes (media_player) →
    // Artist Highlights → Shop preview → Events → the rest. Sections that have no content still
    // collapse gracefully via their `showXxx` + `xxx.length > 0` guards.
    const sectionOrder = useMemo(() => {
        const defaults = ["media_player", "radio", "artists", "roster_grid", "editorial", "shop", "events", "featured_artist", "categories", "about", "manifesto", "join", "subscribe"];
        const raw = (siteContent.home_section_order || defaults.join(","))
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        const inRaw = new Set(raw);
        return [...raw, ...defaults.filter((d) => !inRaw.has(d))];
    }, [siteContent.home_section_order]);

    // Per-section subtitle/title toggles
    const kickerHero = useSiteFlag("section_show_kicker_hero", true);
    const kickerShop = useSiteFlag("section_show_kicker_shop", true);
    const kickerFeatured = useSiteFlag("section_show_kicker_featured_artist", true);
    const kickerArtists = useSiteFlag("section_show_kicker_artists", true);
    const kickerEvents = useSiteFlag("section_show_kicker_events", true);
    const kickerManifesto = useSiteFlag("section_show_kicker_manifesto", true);
    const titleShop = useSiteFlag("section_show_title_shop", false);
    const titleFeatured = useSiteFlag("section_show_title_featured_artist", true);
    const titleArtists = useSiteFlag("section_show_title_artists", true);
    const titleEvents = useSiteFlag("section_show_title_events", false);

    return (
        <div data-testid="home-page" className="flex flex-col">
            <Seo
                title="ITZKATU — colectivo creativo underground · San Cristóbal de las Casas"
                description="música, arte visual y cultura underground desde chiapas — artistas, radio, eventos, drops y tienda."
                path="/"
            />
            {/* HERO — typographic, centered on the page like /artists. Inherits the
                page scheme background from Layout so the "." dot uses --sc-accent. */}
            <section
                data-testid="home-hero"
                className="px-4 md:px-8 pt-8 md:pt-10 pb-8 md:pb-10"
            >
                <div className="max-w-6xl mx-auto flex flex-col gap-4">
                    {kickerHero && (
                        <div className="font-mono text-sm md:text-base uppercase tracking-[0.35em] text-[color:var(--sc-accent)] max-w-[50%]">
                            {st("hero_kicker")}
                        </div>
                    )}
                    <h1 className="itz-hero font-pixel uppercase leading-[1.05] max-w-[60%]">
                        <span className="block">itz</span>
                        <span className="block">katu</span>
                    </h1>
                    <p className="font-body text-sm md:text-base leading-relaxed max-w-[50%] opacity-80">
                        {st("hero_tag")}
                    </p>
                    {(siteContent[`hero_body_${lang}`] || siteContent.hero_body_en) && (
                        <p className="font-body text-sm md:text-base leading-relaxed max-w-[50%] whitespace-pre-line opacity-70" data-testid="hero-body">
                            {siteContent[`hero_body_${lang}`] || siteContent.hero_body_en}
                        </p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 max-w-[50%]">
                        <Link
                            to="/artists"
                            data-testid="hero-cta-roster"
                            className="inline-flex items-center gap-2 border border-current hover:bg-[color:var(--sc-text)] hover:text-[color:var(--sc-bg)] transition-colors px-4 py-3 font-mono text-sm md:text-base tracking-[0.05em]"
                        >
                            {(() => { const s = st("hero_cta_roster") || ""; return s.charAt(0).toUpperCase() + s.slice(1); })()} <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/shop"
                            data-testid="hero-cta-shop"
                            className="inline-flex items-center gap-2 bg-[color:var(--sc-accent)] text-white hover:opacity-80 transition-opacity px-4 py-3 font-mono text-sm md:text-base tracking-[0.05em]"
                        >
                            {lang === "es" ? "Tienda" : "Shop"} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {siteContent.homepage_music_url && (
                <section
                    data-testid="home-music-widget"
                    className="px-4 md:px-8 py-8 md:py-10"
                >
                    <div className="max-w-6xl mx-auto grid grid-cols-12 gap-4 md:gap-6 items-center">
                        <div className="col-span-12 md:col-span-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--sc-accent)] mb-2">now playing</div>
                            <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight leading-none">
                                {siteContent[`homepage_music_title_${lang}`] || (lang === "es" ? "sonando ahora" : "on rotation")}
                            </h2>
                            <p className="mt-3 font-body text-sm leading-relaxed max-w-md opacity-70">
                                {siteContent[`homepage_music_caption_${lang}`] || (lang === "es" ? "una selección del colectivo. dale play." : "a pick from the collective. hit play.")}
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-8">
                            <iframe
                                title="collective music"
                                src={toEmbedSrc(siteContent.homepage_music_url)}
                                width="100%"
                                height={embedKind(siteContent.homepage_music_url) === "audio" ? "232" : "300"}
                                frameBorder="0"
                                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                loading="lazy"
                                className="block w-full"
                                data-testid="home-music-iframe"
                            />
                        </div>
                    </div>
                </section>
            )}

            {/* CATEGORIES — big image tiles (classyoutlaw-style). Editable & toggleable. */}
            {showCategories && (
                <CategoriesGrid
                    order={sectionOrder.indexOf("categories") + 1}
                    lang={lang}
                    siteContent={siteContent}
                />
            )}

            {/* SHOP — quiet preview. On the homepage, products appear as objects in the
                wider ITZKATU world (small caption, no big price stamp). The /shop page
                keeps full merchandising info. Optional bg image + title/subtitle + own
                color scheme so admin can make this section its own "room". */}
            {showShop && topProducts.length > 0 && (() => {
                const shopBg = siteContent.home_shop_bg_image_url || "";
                // Prefer section_scheme_shop (set from "home sections" admin tab).
                // Fall back to home_shop_scheme (legacy key from shop-front tab).
                const shopSchemeName = siteContent.section_scheme_shop || siteContent.home_shop_scheme || "";
                const shopSch = shopSchemeName ? schemeByName(shopSchemeName) : null;
                const shopHex = shopSchemeName ? schemeHex(shopSchemeName) : null;
                const shopKicker = siteContent[`shop_kicker_${lang}`] || siteContent.shop_kicker_en || "";
                const shopTitle = siteContent[`title_shop_${lang}`] || siteContent.title_shop_en || "";
                const shopSubtitle = siteContent[`body_shop_${lang}`] || siteContent.body_shop_en || "";
                const showShopKicker = !!shopKicker && siteContent.home_shop_show_kicker !== "0";
                const showShopTitle = !!shopTitle && siteContent.home_shop_show_title !== "0";
                const showShopSubtitle = !!shopSubtitle && siteContent.home_shop_show_subtitle !== "0";
                const bannerSize = siteContent.shop_banner_size || "md"; // sm | md | lg
                const bannerSizeCls = {
                    sm: "text-[10px] md:text-xs py-2.5 px-3 tracking-[0.18em]",
                    md: "text-[11px] md:text-xs py-3 px-4 tracking-[0.2em]",
                    lg: "text-sm md:text-base py-4 px-5 tracking-[0.15em]",
                }[bannerSize] || "text-[11px] md:text-xs py-3 px-4 tracking-[0.2em]";
                const sectionCls = `${shopSch ? `${shopSch.bg} ${shopSch.text}` : ""} px-4 md:px-8 pb-6 md:pb-8 relative`;
                const shopFx = siteContent.home_shop_effects === "1";
                const sectionStyle = { order: sectionOrder.indexOf("shop") + 1 };
                if (shopHex) {
                    // Override the layout CSS vars for this section so all
                    // children (canvas effects, dots, banners) adopt the palette.
                    sectionStyle["--sc-bg"] = shopHex.bg;
                    sectionStyle["--sc-text"] = shopHex.text;
                    sectionStyle["--sc-subtext"] = shopHex.subtext;
                    sectionStyle["--sc-accent"] = shopHex.accent;
                    sectionStyle["--sc-accent2"] = shopHex.accent2;
                    sectionStyle["--sc-border"] = shopHex.border;
                    sectionStyle["--sc-card"] = shopHex.card;
                }
                if (shopBg) {
                    sectionStyle.backgroundImage = `url(${shopBg})`;
                    sectionStyle.backgroundSize = "cover";
                    sectionStyle.backgroundPosition = "center";
                }
                return (
                <section
                    data-testid="home-shop-grid"
                    style={sectionStyle}
                    className={sectionCls}
                >
                    {shopBg && <div aria-hidden="true" className="absolute inset-0 bg-black/40 pointer-events-none" />}
                    <div className="relative max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                    {(showShopKicker || showShopTitle || showShopSubtitle) && (
                        <div className="mb-8 md:mb-10 text-left space-y-3" data-testid="home-shop-header">
                            {showShopKicker && (
                                <div className={`font-mono text-xs md:text-sm uppercase tracking-[0.35em] ${shopBg ? "text-white/80" : "text-[color:var(--sc-accent)]"}`}>
                                    {shopKicker}
                                </div>
                            )}
                            {showShopTitle && (
                                <h2 className={`itz-display font-heading font-black uppercase tracking-tighter leading-[0.9] ${shopBg ? "text-white drop-shadow" : ""}`}>
                                    {shopTitle.replace(/\.$/, "")}
                                </h2>
                            )}
                            {showShopSubtitle && (
                                <p className={`font-body text-base md:text-lg leading-relaxed whitespace-pre-line max-w-2xl ${shopBg ? "text-white/85" : "opacity-80"}`}>
                                    {shopSubtitle}
                                </p>
                            )}
                        </div>
                    )}
                    {/* Coupon/shop banner — click-to-copy modal supported. Admin can
                        force-hide via `shop_banner_show === "0"`, otherwise it shows
                        whenever there's content in shop_banner_{lang}. */}
                    {siteContent.shop_banner_show !== "0" && (siteContent[`shop_banner_${lang}`] || siteContent.shop_banner_en) && (
                        <div className="mb-8 max-w-2xl mx-auto">
                            <ShopBanner siteContent={siteContent} lang={lang} sizeCls={bannerSizeCls} />
                        </div>
                    )}
                    <Chips options={shopTypes} value={shopType} onChange={setShopType} testPrefix="home-shop" />
                    {/* Home shop preview — matches /shop grid aesthetic:
                        4 tiles per row, rectangular tall (3/4), white bg,
                        object-contain (no zoom/crop). Title typography is
                        unified with the scene tile titles. */}
                    <div
                        data-testid="home-shop-preview"
                        className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 md:gap-x-16 lg:gap-x-28 xl:gap-x-36 md:gap-y-12"
                    >
                        {shopPreview.map((p) => (
                            <Link
                                key={p.id}
                                to={`/shop/${p.id}`}
                                data-testid={`home-product-${p.id}`}
                                className="group flex flex-col items-start text-left"
                            >
                                <div className="w-full aspect-[3/4] overflow-hidden relative flex items-center justify-center">
                                    {p.image_url && shopFx ? (
                                        <>
                                            <div className="absolute inset-0">
                                                <MediaRenderer
                                                    src={p.image_url}
                                                    alt={p.title}
                                                    preset={derivePreset(p.id || p.title)}
                                                    seed={p.id || p.title}
                                                />
                                            </div>
                                            <img
                                                src={p.image_url_alt || p.image_url}
                                                alt=""
                                                aria-hidden="true"
                                                loading="lazy"
                                                referrerPolicy="no-referrer"
                                                className="absolute inset-0 m-auto max-w-full max-h-full w-auto h-auto object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                            />
                                        </>
                                    ) : (
                                    <>
                                    {p.image_url && (
                                        <img
                                            src={p.image_url}
                                            alt={p.title}
                                            loading="lazy"
                                            referrerPolicy="no-referrer"
                                            className="max-w-full max-h-full w-auto h-auto object-contain transition-opacity duration-500"
                                        />
                                    )}
                                    {p.image_url_alt && (
                                        <img
                                            src={p.image_url_alt}
                                            alt=""
                                            aria-hidden="true"
                                            loading="lazy"
                                            referrerPolicy="no-referrer"
                                            className="absolute inset-0 m-auto max-w-full max-h-full w-auto h-auto object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                        />
                                    )}
                                    </>
                                    )}
                                </div>
                                <h3 className={`mt-3 font-heading font-black text-lg md:text-xl uppercase tracking-tight leading-[1.1] group-hover:text-[color:var(--sc-accent)] transition-colors ${shopBg ? "text-white" : ""}`}>
                                    {p.title}
                                </h3>
                                {typeof p.price === "number" && !isNaN(p.price) && (() => {
                                    const displayCurrency = currency === "MXN" ? "MXN" : "USD";
                                    const base = p.currency === "MXN" ? "MXN" : (p.currency === "EUR" ? "EUR" : "USD");
                                    const fx = parseFloat(siteContent.fx_usd_to_mxn || "20") || 20;
                                    const final = p.sale_percent > 0 ? Number(p.price) * (1 - p.sale_percent / 100) : Number(p.price);
                                    let v = final;
                                    if (base === "USD" && displayCurrency === "MXN") v = final * fx;
                                    else if (base === "MXN" && displayCurrency === "USD") v = final / fx;
                                    const fmt = (n) => Number(n).toFixed(2).replace(/\.?0+$/, "");
                                    return (
                                        <div className={`mt-1 font-mono text-xs md:text-sm ${shopBg ? "text-white/85" : "opacity-80"}`} data-testid={`home-product-price-${p.id}`}>
                                            ${fmt(v)} {displayCurrency}
                                        </div>
                                    );
                                })()}
                            </Link>
                        ))}
                    </div>
                    {siteContent.home_show_explore_shop_cta !== "0" && (
                    <div className="mt-8 flex justify-end">
                        <Link
                            to="/shop"
                            data-testid="home-shop-all"
                            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white hover:text-[color:var(--sc-accent)] transition-colors"
                        >
                            {lang === "es" ? "ver todo" : "see all"} <ArrowRight size={14} />
                        </Link>
                    </div>
                    )}
                    </div>
                </section>
                );
            })()}
            {/* FEATURED ARTIST — media-first tiles. Click navigates DIRECTLY to the
                artist's dedicated page — no intermediate modal. Artists are represented
                by their work (image_url_alt > image_url). */}
            {showFeaturedArtist && featuredList.length > 0 && (() => {
                const s = getSectionSchemeStyles(siteContent, "featured_artist");
                return (
                <section
                    data-testid="home-featured-artist"
                    style={{ order: sectionOrder.indexOf("featured_artist") + 1, ...s.style }}
                    className={`${s.className} px-4 md:px-8 pb-6 md:pb-8`}
                >
                    <div className="max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                    <SectionHeader
                        testId="home-featured-header"
                        kicker={kickerFeatured ? (siteContent[`featured_artist_kicker_${lang}`] || siteContent.featured_artist_kicker_en || "") : ""}
                        title={titleFeatured ? (siteContent[`title_featured_artist_${lang}`] || siteContent.title_featured_artist_en || "") : ""}
                        body={siteContent[`body_featured_artist_${lang}`] || siteContent.body_featured_artist_en || ""}
                    />
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 md:gap-x-8 md:gap-y-14">
                        {filteredFeatured.map((f, i) => {
                            const disc = artistDisciplines(f).join(" · ") || f.discipline || "";
                            const primaryEmbed = (f.embeds && f.embeds[0]) || "";
                            const cover = f.image_url_alt || f.image_url || "";
                            const aspect = i % 5 === 2 ? "tall" : "square";
                            return (
                                <MediaTile
                                    key={f.id}
                                    testId={`home-featured-artist-${f.slug}`}
                                    title={f.name}
                                    caption={disc}
                                    image_url={cover}
                                    image_url_alt={f.image_url}
                                    embed_url={primaryEmbed}
                                    render_preset={f.render_preset}
                                    render_asset_url={f.render_asset_url}
                                    auto_preset
                                    scheme={f.scheme}
                                    seed={f.slug || f.name}
                                    aspect={aspect}
                                    href={`/artists/${f.slug}`}
                                    playHref={`/artists/${f.slug}`}
                                    playSubtitle={disc}
                                />
                            );
                        })}
                    </div>
                    </div>
                </section>
                );
            })()}

            {/* ROSTER GRID — the /artists page grid as an optional home section.
                Shows FEATURED artists only (falls back to all if none are marked). */}
            {showRosterGrid && artists.length > 0 && (() => {
                const s = getSectionSchemeStyles(siteContent, "roster_grid");
                const marked = artists.filter((a) => a.home_featured);
                const baseList = marked.length ? marked : artists;
                const discs = allDisciplines(baseList);
                const shown = rosterGridFilter === "all" ? baseList : baseList.filter((a) => artistDisciplines(a).includes(rosterGridFilter));
                return (
            <section
                    data-testid="home-roster-grid"
                    style={{ order: sectionOrder.indexOf("roster_grid") + 1, ...s.style }}
                    className={`${s.className} px-4 md:px-8 pb-6 md:pb-8`}
            >
                <div className="max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                    <SectionHeader
                        testId="home-roster-grid-header"
                        kicker={siteContent.section_show_kicker_roster_grid !== "0" ? (siteContent[`roster_grid_kicker_${lang}`] || siteContent.roster_grid_kicker_en || "") : ""}
                        title={siteContent.section_show_title_roster_grid !== "0" ? (siteContent[`title_roster_grid_${lang}`] || siteContent.title_roster_grid_en || "") : ""}
                        body={siteContent[`body_roster_grid_${lang}`] || siteContent.body_roster_grid_en || ""}
                    />
                    <Chips options={discs} value={rosterGridFilter} onChange={setRosterGridFilter} testPrefix="home-roster" />
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 md:gap-x-16 lg:gap-x-28 xl:gap-x-36 md:gap-y-12">
                        {shown.map((a) => (
                            <ArtistTile key={a.id || a.slug} artist={a} />
                        ))}
                    </div>
                    <div className="mt-8 flex justify-end">
                        <Link to="/artists" data-testid="home-roster-see-all" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white hover:text-[color:var(--sc-accent)] transition-colors">
                            {lang === "es" ? "ver todo" : "see all"} <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
                );
            })()}

            {/* EDITORIAL BANNER — underground magazine feature of ONE artist.
                Sits between artists and shop; bigger than normal cards to break the grid.
                Admin picks the artist via `editorial_artist_slug` (empty/random = random). */}
            {showEditorial && artists.length > 0 && (() => {
                const s = getSectionSchemeStyles(siteContent, "editorial");
                const slug = (siteContent.editorial_artist_slug || "").trim().toLowerCase();
                const chosen = slug && slug !== "random" ? artists.find((a) => (a.slug || "").toLowerCase() === slug) : null;
                const feat = chosen || artists[edSeed % artists.length];
                if (!feat) return null;
                const showK = siteContent.section_show_kicker_editorial !== "0";
                const kicker = showK ? (siteContent[`editorial_kicker_${lang}`] || siteContent.editorial_kicker_en || "feature") : "";
                const showT = siteContent.section_show_title_editorial !== "0";
                const titleTxt = siteContent[`title_editorial_${lang}`] || siteContent.title_editorial_en || feat.name;
                const desc = siteContent[`body_editorial_${lang}`] || siteContent.body_editorial_en
                    || feat.short_description || [artistDisciplines(feat).join(" · "), feat.location].filter(Boolean).join(" — ");
                return (
            <section
                    data-testid="home-editorial"
                    style={{ order: sectionOrder.indexOf("editorial") + 1, ...s.style }}
                    className={`${s.className} px-4 md:px-8 pb-6 md:pb-8`}
            >
                <div className="max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                    <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
                        <Link to={`/artists/${feat.slug}`} className="col-span-12 md:col-span-7 block group" data-testid="editorial-artwork">
                            <div className="aspect-[4/3] overflow-hidden relative">
                                <MediaRenderer
                                    src={feat.image_url}
                                    alt={feat.name}
                                    preset={feat.render_preset || derivePreset(feat.slug || feat.name)}
                                    seed={feat.slug || feat.name}
                                />
                            </div>
                        </Link>
                        <div className="col-span-12 md:col-span-5">
                            {kicker && <div className="font-mono text-xs uppercase tracking-[0.35em] text-[color:var(--sc-accent)] mb-3">{kicker}</div>}
                            {showT && <h2 className="itz-hero font-heading font-black uppercase tracking-tighter leading-[0.9]">{titleTxt}</h2>}
                            {desc && (
                                <p className="mt-3 font-body text-base md:text-lg leading-relaxed opacity-80 max-w-md">{desc}</p>
                            )}
                            <Link
                                to={`/artists/${feat.slug}`}
                                data-testid="editorial-view-artist"
                                className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] underline decoration-1 underline-offset-4 hover:text-[color:var(--sc-accent)] transition-colors"
                            >
                                {lang === "es" ? "ver artista" : "view artist"} →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
                );
            })()}

            {/* COLLECTIVE — text roster (kept). Made lighter to match the calmer rhythm. */}
            {showArtists && rosterList.length > 0 && (() => {
                const s = getSectionSchemeStyles(siteContent, "artists");
                return (
            <section
                    data-testid="home-artists"
                    style={{ order: sectionOrder.indexOf("artists") + 1, ...s.style }}
                    className={`${s.className} px-4 md:px-8 pb-6 md:pb-8 relative`}
            >
                <div className="max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                <SectionHeader
                    testId="home-artists-header"
                    kicker={kickerArtists ? st("section_roster") : ""}
                    title={titleArtists ? (siteContent[`title_artists_${lang}`] || siteContent.title_artists_en || "") : ""}
                    body={siteContent[`body_artists_${lang}`] || siteContent.body_artists_en || ""}
                />

                {/* Discipline filter as hashtags — Ksawery-style. Bigger, italic
                    hashtags with underline for active. "all" has no # prefix. */}
                <div className="mb-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
                    {homeDisciplines.map((d) => {
                        const isAll = d === "all";
                        const label = isAll ? "all" : `#${d.replace(/\s+/g, "").toLowerCase()}`;
                        const active = homeFilter === d;
                        return (
                            <button
                                key={d}
                                data-testid={`home-filter-${d}`}
                                onClick={() => setHomeFilter(d)}
                                className={`font-body text-lg md:text-2xl leading-tight transition-opacity ${
                                    active
                                        ? "opacity-100 underline underline-offset-4 decoration-2"
                                        : "opacity-50 hover:opacity-100 no-underline"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                <div
                    className="relative"
                    onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setArtistPreview((prev) => (prev ? { ...prev, x: e.clientX - rect.left, y: e.clientY - rect.top } : prev));
                    }}
                >
                    <p className="itz-preserve font-heading font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight break-words relative z-10 uppercase">
                        {filteredRoster.length === 0 ? (
                            <span className="opacity-40 text-2xl md:text-3xl">no artists in this discipline yet.</span>
                        ) : (
                            <>
                                {filteredRoster.map((a, i) => (
                                    <span key={a.id}>
                                        <Link
                                            to={`/artists/${a.slug}`}
                                            data-testid={`home-artist-name-${a.slug}`}
                                            onMouseEnter={(evt) => {
                                                const parent = evt.currentTarget.closest("div.relative");
                                                const rect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
                                                setArtistPreview({
                                                    url: a.image_url_alt || a.image_url,
                                                    name: a.name,
                                                    discipline: artistDisciplines(a).join(" · ") || a.discipline,
                                                    location: a.location,
                                                    accentHex: a.scheme ? schemeHex(a.scheme).accent : "",
                                                    x: evt.clientX - rect.left,
                                                    y: evt.clientY - rect.top,
                                                });
                                            }}
                                            onMouseLeave={() => setArtistPreview(null)}
                                            style={
                                                artistPreview && artistPreview.name === a.name && artistPreview.accentHex
                                                    ? { color: artistPreview.accentHex }
                                                    : undefined
                                            }
                                            className={`transition-colors duration-200 ${
                                                artistPreview && artistPreview.name === a.name
                                                    ? "text-[color:var(--sc-accent)]"
                                                    : artistPreview
                                                    ? "opacity-25"
                                                    : "hover:text-[color:var(--sc-accent)]"
                                            }`}
                                        >
                                            {a.name}
                                        </Link>
                                        {i < filteredRoster.length - 1 && (
                                            <span className={`transition-colors duration-200 ${artistPreview ? "opacity-25" : ""}`}>, </span>
                                        )}
                                    </span>
                                ))}
                                <span>.</span>
                            </>
                        )}
                    </p>

                    {/* Hover portrait — floats OVER the text (desktop only). Reduced to 180px = 50%+ smaller than the Bandcamp reference. */}
                    {artistPreview && artistPreview.url && (
                        <div
                            data-testid="home-artist-portrait"
                            aria-hidden="true"
                            className="pointer-events-none absolute z-20 hidden md:block"
                            style={{
                                left: Math.max(0, artistPreview.x - 90),
                                top: Math.max(0, artistPreview.y - 40),
                                width: 180,
                            }}
                        >
                            {/* Image duotoned to the artist's OWN scheme accent
                                (falls back to the section accent when the artist
                                has no scheme set). Grayscale + multiply keeps the
                                composition while pushing color into the artist's
                                assigned world. */}
                            <div
                                className="aspect-[3/4] overflow-hidden shadow-2xl relative"
                                style={{ backgroundColor: artistPreview.accentHex || "var(--sc-accent)" }}
                            >
                                <img
                                    src={artistPreview.url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    style={{ filter: "grayscale(1) contrast(1.15)", mixBlendMode: "multiply" }}
                                />
                            </div>
                            {/* Meta — inherits the artist's accent as its background,
                                white text always reads clearly on top. Admin toggle:
                                home_roster_preview_show_meta. */}
                            {siteContent.home_roster_preview_show_meta !== "0" && (
                                <div
                                    className="font-mono text-[9px] uppercase tracking-[0.2em] py-1 px-2 text-center text-white"
                                    style={{ backgroundColor: artistPreview.accentHex || "var(--sc-accent)" }}
                                >
                                    {artistPreview.discipline}
                                    {artistPreview.location && (
                                        <>
                                            <br />
                                            {artistPreview.location}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {/* Full roster link — small uppercase mono, no italic. Toggle from admin. */}
                {siteContent.home_show_full_roster_cta !== "0" && (
                <div className="mt-10 text-center">
                    <Link
                        to="/artists"
                        data-testid="home-artists-all"
                        className="inline-block font-mono text-[10px] uppercase tracking-[0.22em] opacity-70 hover:opacity-100 hover:text-[color:var(--sc-accent)] underline decoration-1 underline-offset-4 transition-colors"
                    >
                        {siteContent[`home_full_roster_cta_${lang}`] || siteContent.home_full_roster_cta_en || (lang === "es" ? "ver roster completo" : "see the full roster")} →
                    </Link>
                </div>
                )}
                </div>
            </section>
            );
            })()}

            {/* EVENTS — quiet list. No large title, only a small kicker if the admin wants it. */}
            {showEvents && events.length > 0 && (() => {
                const s = getSectionSchemeStyles(siteContent, "events");
                return (
                <section
                    data-testid="home-events"
                    style={{ order: sectionOrder.indexOf("events") + 1, ...s.style }}
                    className={`${s.className} px-4 md:px-8 pb-6 md:pb-8`}
                >
                    <div className="max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                    <SectionHeader
                        testId="home-events-header"
                        kicker=""
                        title={titleEvents ? (siteContent[`title_events_${lang}`] || siteContent.title_events_en || "") : ""}
                        body={siteContent[`body_events_${lang}`] || siteContent.body_events_en || ""}
                    />
                    <div ref={eventsRef} onMouseMove={handleEventMove} className="relative max-w-4xl mx-auto border-y border-current/10">
                        <ul>
                            {events.map((e) => {
                                const flyerImg = e.flyer_url || e.image_url;
                                return (
                                    <li
                                        key={e.id}
                                        data-testid={`home-event-row-${e.id}`}
                                        onMouseEnter={(evt) => {
                                            if (!eventsRef.current || !flyerImg) return;
                                            const rect = eventsRef.current.getBoundingClientRect();
                                            setFlyer({ url: flyerImg, x: evt.clientX - rect.left, y: evt.clientY - rect.top });
                                        }}
                                        onMouseLeave={() => setFlyer(null)}
                                        className="relative grid grid-cols-12 items-center gap-4 py-5 md:py-6 group hover:bg-[#333] hover:text-[#FFFFFF] transition-colors duration-300 before:absolute before:top-0 before:left-4 before:right-4 md:before:left-10 md:before:right-10 before:h-px before:bg-[#333]/15 first:before:hidden group-hover:before:bg-transparent"
                                    >
                                        <div className="col-span-4 md:col-span-2 font-mono text-xs md:text-sm uppercase tracking-[0.2em]">
                                            {fmtEventDateShort(e.date, e.country, lang)}
                                        </div>
                                        <div className="col-span-8 md:col-span-5 font-heading font-bold text-xl md:text-2xl uppercase tracking-tight">
                                            {e.title}
                                        </div>
                                        <div className="col-span-8 md:col-span-3 font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                                            <MapPin size={12} />
                                            {e.venue_url ? (
                                                <a href={e.venue_url} target="_blank" rel="noreferrer" className="link-underline">{e.venue}</a>
                                            ) : (
                                                <span>{e.venue}</span>
                                            )}
                                            <span>· {e.city}</span>
                                        </div>
                                        <div className="col-span-4 md:col-span-2 text-right">
                                            {e.ticket_url ? (
                                                <a
                                                    href={e.ticket_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`home-event-ticket-${e.id}`}
                                                    className="font-mono text-xs uppercase tracking-[0.2em] link-underline group-hover:text-[#5D3FD3]"
                                                >
                                                    {t("tickets")}
                                                </a>
                                            ) : (e.facebook_url || e.instagram_url) ? (
                                                <div className="inline-flex items-center gap-3 justify-end" data-testid={`home-event-social-${e.id}`}>
                                                    {e.instagram_url && (
                                                        <a href={e.instagram_url} target="_blank" rel="noreferrer" aria-label="instagram" className="hover:text-[#5D3FD3] transition-colors">
                                                            <Instagram size={16} />
                                                        </a>
                                                    )}
                                                    {e.facebook_url && (
                                                        <a href={e.facebook_url} target="_blank" rel="noreferrer" aria-label="facebook" className="hover:text-[#5D3FD3] transition-colors">
                                                            <Facebook size={16} />
                                                        </a>
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        {flyer && flyer.url && (
                            <div
                                data-testid="home-event-flyer"
                                aria-hidden="true"
                                className="pointer-events-none absolute z-40 hidden md:block"
                                style={{ left: flyer.x + 24, top: flyer.y - 140, width: 220, height: 300 }}
                            >
                                <img src={flyer.url} alt="" className="w-full h-full object-cover shadow-2xl" />
                            </div>
                        )}
                    </div>
                    <div className="mt-8 text-center">
                        <Link
                            to="/events"
                            data-testid="link-all-events"
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#333] hover:text-[#5D3FD3] underline decoration-1 underline-offset-4 transition-colors"
                        >
                            {st("full_agenda")} <ArrowRight size={12} />
                        </Link>
                    </div>
                    </div>
                </section>
                );
            })()}

            {/* ABOUT — editorial two-column block */}
            {showAbout && (() => {
                // About inherits the page scheme unless the admin sets one explicitly.
                const aboutName = (siteContent.section_scheme_about || "").trim();
                const sa = aboutName ? schemeByName(aboutName) : null;
                const saHex = aboutName ? schemeHex(aboutName) : null;
                const saStyle = { order: sectionOrder.indexOf("about") + 1 };
                if (saHex) {
                    saStyle["--sc-bg"] = saHex.bg;
                    saStyle["--sc-text"] = saHex.text;
                    saStyle["--sc-subtext"] = saHex.subtext;
                    saStyle["--sc-accent"] = saHex.accent;
                    saStyle["--sc-accent2"] = saHex.accent2;
                    saStyle["--sc-border"] = saHex.border;
                    saStyle["--sc-card"] = saHex.card;
                }
                return (
                <section
                    data-testid="home-about"
                    style={saStyle}
                    className={`${sa ? `${sa.bg} ${sa.text}` : ""} px-4 md:px-8 pb-6 md:pb-8`}
                >
                    <div className="max-w-6xl mx-auto border-t border-current/10 pt-6 md:pt-8">
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        <div className="col-span-12 md:col-span-4">
                            <div className={`font-mono text-[10px] uppercase tracking-[0.35em] ${sa ? sa.kicker : "text-[color:var(--sc-accent)]"} mb-3`}>
                                {siteContent[`about_kicker_${lang}`] || siteContent.about_kicker_en || "about the collective"}
                            </div>
                            <h2 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tight leading-none">
                                {(siteContent[`about_title_${lang}`] || siteContent.about_title_en || (lang === "es" ? "quiénes somos" : "who we are")).replace(/\.$/, "")}<span className={sa ? sa.dotColor : "text-[color:var(--sc-accent)]"}>.</span>
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-8">
                            <div className={`font-body text-base md:text-lg ${sa ? sa.subtext : "opacity-80"} leading-relaxed max-w-[62ch] whitespace-pre-line`}>
                                {siteContent[`about_body_${lang}`] || siteContent.about_body_en || (lang === "es"
                                    ? "itzkatu es un colectivo creativo de artistas, productores y visuales basado en San Cristóbal de las Casas. proyectos colaborativos, eventos, ediciones limitadas, y estructura transparente para que cada integrante pueda crecer con el colectivo."
                                    : "itzkatu is a creative collective of artists, producers, and visual creators based in San Cristóbal de las Casas. collaborative projects, events, limited editions — with a transparent structure so every member can grow alongside the collective.")}
                            </div>
                        </div>
                    </div>
                    </div>
                </section>
                );
            })()}

            {/* MULTIMEDIA LAB removed 2026-07-20 — replaced by MediaPlayerSection below */}

            {/* MANIFESTO — quiet centered column */}
            {showManifesto && (
            <section
                style={{ order: sectionOrder.indexOf("manifesto") + 1 }}
                className="bg-white px-4 md:px-8 py-12 md:py-16 grid grid-cols-12 gap-6"
            >
                <div className="col-span-12 md:col-span-6 md:col-start-4">
                    {kickerManifesto && (
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#777] text-center mb-6">
                            {st("section_manifesto")}
                        </div>
                    )}
                    <p className="font-body text-base md:text-lg text-[#333] leading-relaxed">
                        {st("manifesto_body_1")}
                    </p>
                    <p className="mt-4 font-body text-base md:text-lg text-[#333] leading-relaxed">
                        {st("manifesto_body_2")}
                    </p>
                    <div className="mt-6 text-center">
                        <Link to="/about" data-testid="link-studio" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#333] hover:text-[#5D3FD3] underline decoration-1 underline-offset-4 transition-colors">
                            {st("manifesto_cta")} <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* SCENE — curated editorial features. Tiles link out, NO players. */}
            {showMediaPlayer && (
                <SceneSection
                    order={sectionOrder.indexOf("media_player") + 1}
                    scope="home"
                    channel="scene"
                />
            )}

            {/* ITZKATU RADIO — the scene's twin WITH play buttons (sticky player). */}
            {showRadio && (
                <SceneSection
                    order={sectionOrder.indexOf("radio") + 1}
                    scope="home"
                    channel="radio"
                    testId="home-radio"
                />
            )}

            {/* JOIN THE COLLECTIVE — open invite, toggleable from admin */}
            {showJoin && (
                <JoinSection
                    order={sectionOrder.indexOf("join") + 1}
                    lang={lang}
                    siteContent={siteContent}
                />
            )}
            {/* NEWSLETTER — separate, low-commitment. Toggle from admin → home sections. */}
            {showSubscribe && (
                <SubscribeSection
                    order={sectionOrder.indexOf("subscribe") + 1}
                    lang={lang}
                    siteContent={siteContent}
                />
            )}

            {/* Universal QuickView modal used by featured artists tiles */}
            <QuickView item={quickItem} onClose={() => setQuickItem(null)} />
        </div>
    );
}

// Category tiles grid — big-image tiles with bold uppercase label overlays,
// classyoutlaw-style. All content editable from admin:
//   cats_kicker_{lang}, cats_title_{lang}
//   cats_tile_{N}_image_url  (N = 1..6)
//   cats_tile_{N}_label_{lang}
//   cats_tile_{N}_link
// Tile is skipped if it has no image AND no label.
function CategoriesGrid({ order, lang, siteContent }) {
    const kicker = siteContent[`cats_kicker_${lang}`] || siteContent.cats_kicker_en || (lang === "es" ? "explorá" : "shop");
    const title = siteContent[`cats_title_${lang}`] || siteContent.cats_title_en || (lang === "es" ? "por categoría." : "by category.");
    const tiles = [1, 2, 3, 4, 5, 6]
        .map((n) => ({
            n,
            image: siteContent[`cats_tile_${n}_image_url`] || "",
            label: siteContent[`cats_tile_${n}_label_${lang}`] || siteContent[`cats_tile_${n}_label_en`] || "",
            link: siteContent[`cats_tile_${n}_link`] || "",
        }))
        .filter((t) => t.image || t.label);
    if (tiles.length === 0) return null;
    return (
        <section
            data-testid="home-categories"
            style={{ order }}
            className="bg-[#FFFFFF] px-4 md:px-8 py-8 md:py-12"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-1">{kicker}</div>
                    <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight leading-none text-black">
                        {title.replace(/\.$/, "")}<span className="text-[#5D3FD3]">.</span>
                    </h2>
                </div>
            </div>
            <div className={`grid gap-3 md:gap-4 grid-cols-2 ${tiles.length >= 4 ? "md:grid-cols-4" : `md:grid-cols-${tiles.length}`}`}>
                {tiles.map((t) => {
                    const Inner = (
                        <div
                            className="relative aspect-[3/4] bg-[#EEE] overflow-hidden group"
                            data-testid={`categories-tile-${t.n}`}
                        >
                            {t.image && (
                                <img
                                    src={t.image}
                                    alt={t.label}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                                    referrerPolicy="no-referrer"
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
                            {t.label && (
                                <div className="absolute bottom-3 left-3 right-3 font-heading font-black text-3xl md:text-5xl uppercase tracking-tighter text-white pointer-events-none leading-[0.85] drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                                    {t.label}
                                </div>
                            )}
                        </div>
                    );
                    if (!t.link) return <div key={t.n}>{Inner}</div>;
                    if (t.link.startsWith("/")) return <Link key={t.n} to={t.link}>{Inner}</Link>;
                    return <a key={t.n} href={t.link} target="_blank" rel="noreferrer">{Inner}</a>;
                })}
            </div>
        </section>
    );
}


// MediaPlayerSection removed 2026-07-23 — replaced by editorial <SceneSection>.
// Legacy media_player_* content in site_content was auto-migrated to scene_features.


// Standalone open-call section that lives at the bottom of the home page.
// Copy is fully editable from the admin (join_kicker_{lang}, join_title_{lang},
// join_body_{lang}, join_cta_{lang}) — falls back to sensible defaults.
function JoinSection({ order, lang, siteContent }) {
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [status, setStatus] = useState("");
    const kicker = siteContent[`join_kicker_${lang}`] || siteContent.join_kicker_en || (lang === "es" ? "abrimos convocatoria" : "we're open");
    const title = siteContent[`join_title_${lang}`] || siteContent.join_title_en || (lang === "es" ? "quiero entrar." : "let me in.");
    const body = siteContent[`join_body_${lang}`] || siteContent.join_body_en || (lang === "es"
        ? "artista, productor, visual, curador, técnica — si podés sumar, dejanos tu correo. escribimos si hace click."
        : "artist, producer, visual, curator, tech — if you can add something, drop your email. we'll write back if it clicks.");
    const cta = siteContent[`join_cta_${lang}`] || siteContent.join_cta_en || (lang === "es" ? "enviar" : "send");
    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setStatus("sending");
        try {
            await api.post("/interest", { email: email.trim(), note: note.trim(), source: "home_general" });
            setStatus("ok");
            setEmail(""); setNote("");
        } catch { setStatus("err"); }
    };
    return (
        <section
            data-testid="home-join"
            style={{ order }}
            className="bg-[#5D3FD3] text-[#FFFFFF] px-4 md:px-8 py-12 md:py-16"
        >
            {/* APPLY BLOCK — for people who want to actually join the collective */}
            <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                <div className="col-span-12 md:col-span-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#00E0FF] mb-3">{kicker}</div>
                    <h2 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        {title.replace(/\.$/, "")}<span className="text-[#00E0FF]">.</span>
                    </h2>
                    <p className="mt-5 font-body text-sm md:text-base leading-relaxed text-[#FFFFFF]/80 max-w-md whitespace-pre-line">
                        {body}
                    </p>
                </div>
                <div className="col-span-12 md:col-span-7 md:pl-6">
                    <form onSubmit={submit} data-testid="home-join-form" className="flex flex-col gap-3">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={lang === "es" ? "tu correo" : "your email"}
                            data-testid="home-join-email"
                            className="bg-transparent border border-[#FFFFFF]/50 focus:border-[#00E0FF] outline-none font-mono text-sm md:text-base p-3 text-[#FFFFFF] placeholder:text-[#FFFFFF]/50"
                        />
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={lang === "es" ? "qué te apasiona (opcional)" : "what you're into (optional)"}
                            data-testid="home-join-note"
                            className="bg-transparent border border-[#FFFFFF]/50 focus:border-[#00E0FF] outline-none font-mono text-sm md:text-base p-3 text-[#FFFFFF] placeholder:text-[#FFFFFF]/50"
                        />
                        <button
                            type="submit"
                            disabled={status === "sending"}
                            data-testid="home-join-submit"
                            className="self-start bg-[#FFFFFF] text-[#333] hover:bg-[#111] hover:text-[#FFFFFF] transition-colors px-8 py-3 font-mono text-xs uppercase tracking-[0.3em]"
                        >
                            {status === "sending" ? "…" : cta}
                        </button>
                        {status === "ok" && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E0FF]" data-testid="home-join-ok">
                                {lang === "es" ? "recibido. gracias." : "received. thanks."}
                            </div>
                        )}
                        {status === "err" && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B91C1C]" data-testid="home-join-err">
                                {lang === "es" ? "error — intenta de nuevo." : "error — try again."}
                            </div>
                        )}
                    </form>
                </div>
            </div>

            {/* NEWSLETTER — separate, low-commitment. Just email for updates. */}
        </section>
    );
}

// Newsletter section — visually distinct from JoinSection. Lives at the bottom of the home page.
// Two separate blocks so the "quiero entrar" (invite) and "mantente al día" (newsletter) intents feel different.
function SubscribeSection({ order, lang, siteContent }) {
    const [subEmail, setSubEmail] = useState("");
    const [subStatus, setSubStatus] = useState("");
    const kicker = siteContent[`subscribe_kicker_${lang}`] || siteContent.subscribe_kicker_en || (lang === "es" ? "boletín" : "newsletter");
    const title = siteContent[`subscribe_title_${lang}`] || siteContent.subscribe_title_en || (lang === "es" ? "solo mantenerte al día." : "just stay in the loop.");
    const body = siteContent[`subscribe_body_${lang}`] || siteContent.subscribe_body_en || (lang === "es" ? "drops, eventos, avisos. sin spam." : "drops, events, announcements. no spam.");
    const cta = siteContent[`subscribe_cta_${lang}`] || siteContent.subscribe_cta_en || (lang === "es" ? "suscribirme" : "subscribe");
    const subSubmit = async (e) => {
        e.preventDefault();
        if (!subEmail.trim()) return;
        setSubStatus("sending");
        try {
            await api.post("/subscribe", { email: subEmail.trim(), source: "home_newsletter" });
            setSubStatus("ok");
            setSubEmail("");
        } catch { setSubStatus("err"); }
    };
    return (
        <section
            data-testid="home-subscribe"
            style={{ order }}
            className="bg-[#111] text-white px-4 md:px-8 py-10 md:py-14 border-t border-white/10"
        >
            <div className="grid grid-cols-12 gap-6 md:gap-10 items-center max-w-5xl mx-auto">
                <div className="col-span-12 md:col-span-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#5D3FD3] mb-2">{kicker}</div>
                    <h3 className="font-heading font-black text-2xl md:text-4xl uppercase tracking-tight leading-none">
                        {title.replace(/\.$/, "")}<span className="text-[#5D3FD3]">.</span>
                    </h3>
                    <p className="mt-3 font-body text-sm text-white/70 max-w-md">{body}</p>
                </div>
                <div className="col-span-12 md:col-span-7 md:pl-6">
                    <form onSubmit={subSubmit} data-testid="home-subscribe-form" className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="email"
                            required
                            value={subEmail}
                            onChange={(e) => setSubEmail(e.target.value)}
                            placeholder={lang === "es" ? "tu correo" : "your email"}
                            data-testid="home-subscribe-email"
                            className="flex-1 bg-transparent border border-white/40 focus:border-[#5D3FD3] outline-none font-mono text-sm md:text-base p-3 text-white placeholder:text-white/40"
                        />
                        <button
                            type="submit"
                            disabled={subStatus === "sending"}
                            data-testid="home-subscribe-submit"
                            className="bg-[#5D3FD3] text-white hover:bg-white hover:text-black transition-colors px-6 py-3 font-mono text-xs uppercase tracking-[0.3em]"
                        >
                            {subStatus === "sending" ? "…" : cta}
                        </button>
                    </form>
                    {subStatus === "ok" && (
                        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3]" data-testid="home-subscribe-ok">
                            {lang === "es" ? "suscrito. gracias." : "subscribed. thanks."}
                        </div>
                    )}
                    {subStatus === "err" && (
                        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#B91C1C]" data-testid="home-subscribe-err">
                            {lang === "es" ? "error — intenta de nuevo." : "error — try again."}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

// legacy: JoinSection kept for backward compatibility — the newsletter block is now unused since it moved to SubscribeSection.

// ShopBanner — rich text banner that supports **bold** and *italic* + optional click-through.
// Admin controls: shop_banner_{lang} (the text), shop_banner_click_type (none|shop|coupon),
// shop_banner_coupon_image_url, shop_banner_coupon_code.
function ShopBanner({ siteContent, lang, sizeCls = "text-[11px] md:text-xs py-3 px-4 tracking-[0.2em]" }) {
    const [showCoupon, setShowCoupon] = useState(false);
    const [copied, setCopied] = useState(false);
    const text = siteContent[`shop_banner_${lang}`] || siteContent.shop_banner_en || "";
    const clickType = siteContent.shop_banner_click_type || "none";
    const center = siteContent.shop_banner_center !== "0"; // default centered; admin can set "0" to align left
    const couponImg = siteContent.shop_banner_coupon_image_url || "";
    const couponCode = siteContent.shop_banner_coupon_code || "";
    const shopUrl = siteContent.store_external_url || "/shop";
    const renderMd = (s) => {
        const parts = [];
        let idx = 0;
        const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
        let m;
        while ((m = re.exec(s)) !== null) {
            if (m.index > idx) parts.push(<span key={idx}>{s.slice(idx, m.index)}</span>);
            if (m[2] != null) parts.push(<strong key={m.index} className="font-black">{m[2]}</strong>);
            else if (m[3] != null) parts.push(<em key={m.index} className="italic">{m[3]}</em>);
            idx = m.index + m[0].length;
        }
        if (idx < s.length) parts.push(<span key={"tail-" + idx}>{s.slice(idx)}</span>);
        return parts;
    };
    const inner = <div className={`${center ? "text-center" : "text-left"} leading-relaxed`}>{renderMd(text)}</div>;
    const baseCls = `mb-5 bg-[color:var(--sc-accent)] text-white font-mono uppercase ${sizeCls}`;
    const copy = () => {
        try {
            navigator.clipboard.writeText(couponCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };
    if (clickType === "shop") {
        return (
            <a href={shopUrl} target={shopUrl.startsWith("/") ? "_self" : "_blank"} rel="noreferrer" data-testid="home-shop-banner" className={`${baseCls} block hover:bg-[#111] transition-colors cursor-pointer`}>
                {inner}
            </a>
        );
    }
    if (clickType === "coupon") {
        return (
            <>
                <button type="button" onClick={() => setShowCoupon(true)} data-testid="home-shop-banner" className={`${baseCls} w-full hover:bg-[#111] transition-colors cursor-pointer`}>
                    {inner}
                </button>
                {showCoupon && (
                    <div data-testid="shop-banner-coupon-modal" className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={() => setShowCoupon(false)}>
                        <div className="bg-white text-black w-full max-w-sm p-8" onClick={(e) => e.stopPropagation()}>
                            <div className="text-center">
                                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#777] mb-3">
                                    {lang === "es" ? "cupón" : "coupon"}
                                </div>
                                {couponCode && (
                                    <div className="mx-auto my-6 border border-[#111] px-6 py-4 font-mono text-lg tracking-[0.25em] select-all">
                                        {couponCode}
                                    </div>
                                )}
                                <div className="flex flex-col gap-2">
                                    <button
                                        type="button"
                                        onClick={copy}
                                        data-testid="shop-banner-coupon-copy"
                                        className="w-full border border-[#111] py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] hover:bg-[#111] hover:text-white transition-colors"
                                    >
                                        {copied ? (lang === "es" ? "copiado" : "copied") : (lang === "es" ? "copiar" : "copy")}
                                    </button>
                                    <a
                                        href={shopUrl}
                                        target={shopUrl.startsWith("/") ? "_self" : "_blank"}
                                        rel="noreferrer"
                                        onClick={() => setShowCoupon(false)}
                                        className="w-full bg-[#5D3FD3] text-white py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] hover:bg-black transition-colors"
                                    >
                                        {lang === "es" ? "ir a la tienda" : "open shop"}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }
    return (
        <div data-testid="home-shop-banner" className={baseCls}>
            {inner}
        </div>
    );
}
